import { useState, useEffect, useRef } from "react";
import { SECTIONS } from "@/lib/sections";

/**
 * Returns the ID of the section currently visible in the viewport.
 * A section is "active" when its top edge is at or above 40% of the viewport height.
 * Uses rAF throttling and a passive scroll listener.
 */
export function useActiveSection(): string {
  const [active, setActive] = useState<string>(SECTIONS[0].id);
  const rafId = useRef<number | null>(null);

  useEffect(() => {
    const prefersReduced = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    )?.matches;

    const compute = () => {
      const threshold = window.innerHeight * 0.4;
      let current = SECTIONS[0].id;

      for (const section of SECTIONS) {
        const el = document.getElementById(section.id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= threshold) {
          current = section.id;
        }
      }

      setActive(current);
    };

    const onScroll = () => {
      if (prefersReduced) {
        compute();
        return;
      }
      if (rafId.current !== null) return;
      rafId.current = requestAnimationFrame(() => {
        compute();
        rafId.current = null;
      });
    };

    // Run once on mount
    compute();

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafId.current !== null) cancelAnimationFrame(rafId.current);
    };
  }, []);

  return active;
}
